import type {FetchDefaultHeaders} from '../data-layer-client/buildFetchOverride';
import {
  ACCEPT_LANGUAGE_HEADER,
  ACCOUNT_HEADER,
  LIVEMODE_HEADER,
  STRIPE_VERSION_HEADER,
} from './headerConstants';

/**
 * Transforms HTTP headers into a Record<string, string>
 */
export function transformHeaders(
  inputHeaders?: HeadersInit,
): Record<string, string> {
  if (!inputHeaders) {
    return {};
  }

  if (Array.isArray(inputHeaders) || inputHeaders instanceof Headers) {
    // Array of arrays, or Headers instance
    const outputHeaders: Record<string, string> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const header of inputHeaders) {
      const [key, value] = header;
      outputHeaders[key] = value;
    }
    return outputHeaders;
  }

  // No need to transform Record<string, string>
  return inputHeaders;
}

export const EMPTY_ACCOUNT_HEADER = 'empty_account_header';

/**
 * Adds Stripe specific headers to HTTP headers
 */
export function transformStripeHeaders(
  inputHeaders: Record<string, string>,
  defaultHeaders: FetchDefaultHeaders,
  removeEmptyHeaders = false,
): Record<string, string> {
  if (!defaultHeaders) return inputHeaders;

  const currentHeaders = Object.keys(inputHeaders).map((key) =>
    key.toLowerCase(),
  );

  const inputHeadersKeyLowerCase = Object.keys(inputHeaders).reduce<
    Record<string, string>
  >((acc, key) => {
    acc[key.toLocaleLowerCase()] = inputHeaders[key];
    return acc;
  }, {});

  const headers = {
    ...inputHeadersKeyLowerCase,
  };

  if (
    defaultHeaders.stripeAccount &&
    !currentHeaders.includes(ACCOUNT_HEADER)
  ) {
    headers[ACCOUNT_HEADER] = defaultHeaders.stripeAccount;
  }

  if (removeEmptyHeaders && headers[ACCOUNT_HEADER] === EMPTY_ACCOUNT_HEADER) {
    delete headers[ACCOUNT_HEADER];
  }

  if (
    defaultHeaders.acceptLanguage &&
    !currentHeaders.includes(ACCEPT_LANGUAGE_HEADER)
  ) {
    headers[ACCEPT_LANGUAGE_HEADER] = defaultHeaders.acceptLanguage;
  }
  if (!currentHeaders.includes(STRIPE_VERSION_HEADER)) {
    headers[STRIPE_VERSION_HEADER] = defaultHeaders.stripeVersion;
  }
  if (
    typeof defaultHeaders.stripeLivemode === 'boolean' &&
    !currentHeaders.includes(LIVEMODE_HEADER)
  ) {
    headers[LIVEMODE_HEADER] = defaultHeaders.stripeLivemode.toString();
  }
  return headers;
}
